import * as React from "react";
import { Outlet } from "react-router";

import { useMe, type PrimaryPersona } from "@/api/me";
import { SubNav, type SubNavTab } from "@/components/ui/sub-nav";
import { useAuthStore } from "@/store/authStore";

/**
 * Admin shell (MV2-848, app/AdminIA): three levels flattened into ONE tab
 * row — Clients · Customers · Sites · Users · Policies · Cross-client ·
 * Audit · Platform · Developer. Invitations fold into Users (Pending),
 * Catalogs / register maps / equipment profiles become Developer, Platform
 * settings + health become Platform. Vendor accounts stay in the row until
 * the client record becomes the hub (Slice 6) and absorbs them.
 *
 * Persona-filtered: a SuperAdmin acting as a tenant sees what that tenant's
 * ClientAdmin sees. Tabs a persona lacks are omitted here rather than
 * disabled — admin surfaces have no upgrade path for a ClientAdmin.
 */
interface AdminTab {
  to: string;
  label: string;
  /** Personas that see this tab. Default: SuperAdmin + ClientAdmin. */
  personas?: readonly PrimaryPersona[];
}

const SA: readonly PrimaryPersona[] = ["SuperAdmin"];

export const ADMIN_TABS: readonly AdminTab[] = [
  // Clients is SuperAdmin-only — a ClientAdmin manages exactly one client, via Settings.
  { to: "/admin/clients", label: "Clients", personas: SA },
  { to: "/admin/customers", label: "Customers" },
  { to: "/admin/sites", label: "Sites", personas: SA },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/policies", label: "Policies" },
  { to: "/admin/cross-client", label: "Cross-client" },
  { to: "/admin/audit", label: "Audit", personas: SA },
  { to: "/admin/vendor-accounts", label: "Vendor accounts", personas: SA },
  { to: "/admin/platform", label: "Platform", personas: SA },
  { to: "/admin/developer", label: "Developer", personas: SA },
];

export function adminTabsFor(
  persona: PrimaryPersona | null,
  acting: boolean,
): SubNavTab[] {
  const effective: PrimaryPersona | null =
    persona === "SuperAdmin" && acting ? "ClientAdmin" : persona;
  if (effective === null) return [];
  return ADMIN_TABS.filter((t) =>
    (t.personas ?? ["SuperAdmin", "ClientAdmin"]).includes(effective),
  ).map((t) => ({ to: t.to, label: t.label }));
}

export function AdminShell(): React.ReactElement {
  const { data: me } = useMe();
  const actingAs = useAuthStore((s) => s.actingAs);
  const tabs = React.useMemo(
    () => adminTabsFor(me?.primary_persona ?? null, actingAs !== null),
    [me?.primary_persona, actingAs],
  );

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-gray-200 bg-surface px-[22px] pt-2">
        <div className="type-eyebrow pb-1">Admin</div>
        <SubNav families={[{ tabs }]} />
      </header>
      {/* One scroll surface — the page-level one; per-page wrappers must not add their own. */}
      <div className="mx-auto w-full max-w-[1400px] flex-1 min-h-0 overflow-y-auto px-4 py-6 md:px-6">
        <Outlet />
      </div>
    </div>
  );
}
